import { LucideIcon } from 'lucide-react';
import React, { forwardRef, useEffect, useRef, useState } from 'react';

export interface MenuItem {
  icon?: LucideIcon;
  label?: string;
  onClick?: () => void;
  divider?: boolean;
}

interface IconButtonProps {
  icon: LucideIcon;
  onClick?: () => void;
  title?: string;
  active?: boolean;
  indicator?: boolean;
  className?: string;
  menuItems?: MenuItem[];
  disabled?: boolean;
  children?: React.ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon: Icon,
      onClick,
      title,
      active = false,
      indicator = false,
      className = '',
      menuItems,
      disabled = false,
      children,
    },
    ref
  ) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target as Node)
        ) {
          setIsMenuOpen(false);
        }
      }

      if (isMenuOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isMenuOpen]);

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (menuItems && menuItems.length > 0) {
        setIsMenuOpen((prev) => !prev);
      } else if (onClick) {
        onClick();
      }
    };

    return (
      <div className="relative">
        <button
          ref={ref || buttonRef}
          type="button"
          onClick={handleClick}
          title={title}
          disabled={disabled}
          className={`
          ${
            children
              ? 'px-3 py-2 w-auto h-9 gap-2 text-sm font-medium'
              : 'w-10 h-10'
          }
          flex items-center justify-center 
          text-neutral-600 dark:text-neutral-400 
          bg-white dark:bg-neutral-950 
          border 
          ${
            active || isMenuOpen
              ? 'border-brand-500 dark:border-brand-600 text-brand-600 dark:text-brand-400'
              : 'border-neutral-200 dark:border-neutral-800 hover:border-brand-300 dark:hover:border-brand-700'
          }
          rounded-lg 
          transition-all
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${className}
        `
            .trim()
            .replace(/\s+/g, ' ')}
        >
          <Icon className={children ? 'w-4 h-4' : 'w-5 h-5'} />
          {children}
        </button>

        {indicator && !disabled && (
          <span
            className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-brand-600 ring-2 ring-white dark:ring-neutral-950"
            aria-hidden="true"
          />
        )}

        {menuItems && menuItems.length > 0 && isMenuOpen && (
          <div
            ref={menuRef}
            className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-lg z-50 py-1"
          >
            {menuItems.map((item, index) => {
              if (item.divider) {
                return (
                  <div
                    key={`divider-${index}`}
                    className="border-t border-neutral-200 dark:border-neutral-800 my-1"
                  />
                );
              }

              const ItemIcon = item.icon;

              return (
                <button
                  key={`${item.label}-${index}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.onClick) {
                      item.onClick();
                    }
                    setIsMenuOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left text-xs flex items-center gap-2 text-neutral-700 dark:text-neutral-300 hover:bg-brand-50 dark:hover:bg-brand-950/50 hover:text-brand-900 dark:hover:text-brand-100 transition-colors"
                >
                  {ItemIcon && <ItemIcon className="w-3.5 h-3.5" />}
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

IconButton.displayName = 'IconButton';
